import { CommonModule } from '@angular/common'
import { Component, inject, OnDestroy, OnInit } from '@angular/core'
import { loader, LoaderType } from '@app/decorators/decorators'
import { LoaderDirective } from '@app/dir/loader/loader.directive'
import { BaseModal } from '@app/pages/base.modal'
import { DatePipe } from '@app/pipes/date.pipe'
import { TemperaturePipe } from '@app/pipes/temperature.pipe'
import { BluetoothService } from '@app/srv/bluetooth.service'
import {
  BluetoothThermometerService,
  SyncedTemperature,
} from '@app/srv/bluetooth.thermometer.service'
import { HardwareDeviceService } from '@app/srv/hardwareDevice.service'
import { LogService } from '@app/srv/log.service'
import { Priority } from '@app/srv/popup.cnst'
import { PopupController } from '@app/srv/popup.controller'
import { QAService } from '@app/srv/qa.service'
import {
  AlertInput,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonItemGroup,
  IonLabel,
  IonList,
  IonListHeader,
  IonNote,
  IonSpinner,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone'
import { _numberEnumKeys, IsoDate, localDate, localTime } from '@naturalcycles/js-lib'
import { BatteryStatus, HardwareId } from '@naturalcycles/shared'

@Component({
  selector: 'page-debug-uebe',
  imports: [
    CommonModule,
    LoaderDirective,
    DatePipe,
    TemperaturePipe,
    IonHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonItemGroup,
    IonNote,
    IonSpinner,
    IonListHeader,
    IonFooter,
  ],
  templateUrl: './debug-uebe.page.html',
})
export class DebugUebePage extends BaseModal implements OnInit, OnDestroy {
  className = 'DebugUebePage'

  protected override blockPopups = false

  private bluetoothService = inject(BluetoothService)
  private bluetoothThermometerService = inject(BluetoothThermometerService)
  private hardwareDeviceService = inject(HardwareDeviceService)
  private popupController = inject(PopupController)
  private logService = inject(LogService)
  private qaService = inject(QAService)

  public temperatures: SyncedTemperature[] = []
  public bluetoothEnabled$ = this.bluetoothService.bluetoothEnabled$
  public hardwareDevice$ = this.hardwareDeviceService.hardwareDevice$
  public logs = this.logService.bleLogs

  public native = window.Capacitor.isNativePlatform()
  public initialized = false

  public async ngOnInit(): Promise<void> {
    this.subscriptions.push(
      this.bluetoothThermometerService.temperatures$.subscribe(temperatures => {
        this.temperatures.unshift(...temperatures)
      }),
    )
  }

  public enableBluetooth(): void {
    void this.bluetoothService.enableBluetooth(true)
  }

  @loader(LoaderType.BUTTON)
  public async scanAndPair(): Promise<void> {
    const initialized = await this.bluetoothService.initBluetooth('UebeDebug')

    if (!initialized) return

    await this.bluetoothService.ensureBluetoothEnabled()

    const device = await this.bluetoothService.scanAndPair(HardwareId.UEBE_THERMOMETER)

    if (!device || !device.deviceId) return

    await this.hardwareDeviceService.saveHardwareDevice(
      {
        mac: device.deviceId,
        name: device.name,
        buzzer: true,
        backlight: true,
        testMeasurementDone: false,
        created: localTime.now().unix,
      },
      HardwareId.UEBE_THERMOMETER,
    )
  }

  public fakePair(): void {
    void this.qaService.pairFakeThermometer()
  }

  public async unpair(mac: string): Promise<void> {
    const unpair = confirm('Do you want to unpair?')

    if (!unpair) return

    await this.hardwareDeviceService.clearHardwareDevice(mac)
  }

  public mockTemperature(): void {
    const temp = prompt('Enter temperature or leave empty to get random')
    const value = temp ? Number(temp) : undefined
    const date =
      (prompt("Enter date as YYYY-mm-dd or leave empty to use today's date") as IsoDate) ||
      localDate.todayString()

    this.qaService.mockThermometerTemperatures([{ value, date }])
    void this.dismissModal()
  }

  public mockMulti(): void {
    this.qaService.generateThermometerTemperatures()
    void this.dismissModal()
  }

  public mockMultiSameDay(): void {
    this.qaService.generateThermometerTemperaturesSameDay()
    void this.dismissModal()
  }

  public async setBatteryStatus(): Promise<void> {
    const inputs = _numberEnumKeys(BatteryStatus)
      .filter(label => label !== 'CRITICALLY_LOW')
      .map(label => {
        return {
          type: 'radio',
          label,
          value: BatteryStatus[label],
        }
      }) as AlertInput[]

    await this.popupController.presentAlert(
      {
        header: 'Set battery status',
        inputs,
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            cssClass: 'secondary',
          },
          {
            text: 'Save',
            handler: value => {
              void this.hardwareDeviceService.saveBatteryStatus(value)
            },
          },
        ],
      },
      'alert-t3-algo',
      Priority.IMMEDIATE,
    )
  }

  public async copyLogs(): Promise<void> {
    await this.logService.copyBleLogsToClipboard()
  }

  public clearLogs(): void {
    this.temperatures = []
    this.logService.clearBleLogs()
  }
}
